import { useFlag } from '@automata/utils/hooks';
import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { SxProps } from '@mui/system';
import React, { useCallback, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { BuildingBlocksToolbar } from './BuildingBlocksToolbar';
import ParametersToolbar from './ParametersToolbar';
import {
  ConnectedLabwareSelectionPanel,
  ConnectedTaskSelectionPanel,
} from './selectionPanels';
import { ConditionalFlowSelectionPanel } from './selectionPanels/ConditionalFlowSelectionPanel';
import ViewSettingsPanel from './selectionPanels/ViewSettingsPanel';
import { UndoRedoToolbar } from './UndoRedoToolbar';
import ViewSettingsToolbar from './ViewSettingsToolbar';
import { ConnectedWorkflowDescriptionPanel } from './WorkflowDescriptionPanel';
import { selectSelectionPanel } from '../../features/ui/selectors';
import { closeSelectionPanel } from '../../features/ui/uiSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { NodeType } from '../../types';
import { zIndexValues } from '../styles';

export type Props = {
  isDescriptionPanelExpanded: boolean;
  hideDescriptionPanel: () => void;
};

const noPointerEvents = {
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
} satisfies SxProps<Theme>;

const Toolbars = ({
  isDescriptionPanelExpanded,
  hideDescriptionPanel,
}: Props) => {
  const dispatch = useAppDispatch();
  const currentSelectionPanel = useAppSelector(selectSelectionPanel);
  const apiBatchingEnabled = useFlag('fe.api_batching');

  const buildingBlocksParentRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(() => {
    dispatch(closeSelectionPanel());
  }, [dispatch]);

  useOnClickOutside(buildingBlocksParentRef, handleClickOutside);

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      p={2}
      width="100%"
      height="100%"
      zIndex={zIndexValues.toolbars}
      sx={noPointerEvents}
    >
      <Box
        position="relative"
        width="inherit"
        height="inherit"
        sx={noPointerEvents}
      >
        <Stack
          direction="row"
          sx={[noPointerEvents, { maxHeight: '100%' }]}
          ref={buildingBlocksParentRef}
          gap={2}
        >
          <Stack gap={2}>
            <BuildingBlocksToolbar onOpenPanel={hideDescriptionPanel} />
            {apiBatchingEnabled && <ParametersToolbar />}
            <ViewSettingsToolbar />
          </Stack>

          <Box>
            {isDescriptionPanelExpanded && (
              <ConnectedWorkflowDescriptionPanel />
            )}

            {currentSelectionPanel === NodeType.LABWARE && (
              <ConnectedLabwareSelectionPanel />
            )}

            {currentSelectionPanel === NodeType.TASK && (
              <ConnectedTaskSelectionPanel />
            )}
            {currentSelectionPanel === 'viewSettings' && <ViewSettingsPanel />}
            {currentSelectionPanel === NodeType.CONDITIONAL_TASK && (
              <ConditionalFlowSelectionPanel />
            )}
          </Box>
        </Stack>

        <Box position="absolute" bottom={0} left={0} sx={noPointerEvents}>
          <UndoRedoToolbar />
        </Box>
      </Box>
    </Box>
  );
};

export default Toolbars;
